import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { dropLeadingZeroDays } from '../lib/timeSeries.js';

export interface DailyCostBucket {
  date: string;
  auto_approved_usd: number;
  human_required_usd: number;
}

interface DailyCostChartProps {
  daily: DailyCostBucket[];
}

export function isEmptyDailyCost(daily: DailyCostBucket[]): boolean {
  return daily.every((d) => d.auto_approved_usd === 0 && d.human_required_usd === 0);
}

function fmtUsd(v: number): string {
  return `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function makeAxisUsdFormatter(maxValue: number): (v: number) => string {
  const digits = maxValue < 10 ? 2 : 0;
  return (v) =>
    `$${v.toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits })}`;
}

function fmtDayTick(d: string): string {
  return d.slice(5);
}

export function DailyCostChart({ daily }: DailyCostChartProps) {
  if (isEmptyDailyCost(daily)) {
    return (
      <div className="card" style={{ padding: 'var(--loop-space-200)' }}>
        <div style={{ fontSize: '0.875rem', fontWeight: 664, color: 'var(--loop-neutral-900)', marginBottom: 'var(--loop-space-100)' }}>
          Daily spend
        </div>
        <div style={{ padding: '24px 0', textAlign: 'center', color: 'var(--loop-neutral-500)', fontSize: '0.875rem' }}>
          No spend in this window.
        </div>
      </div>
    );
  }

  const visible = dropLeadingZeroDays(daily, (d) => d.auto_approved_usd === 0 && d.human_required_usd === 0);
  const maxStackedValue = visible.reduce(
    (m, d) => Math.max(m, d.auto_approved_usd + d.human_required_usd),
    0,
  );
  const fmtAxisUsd = makeAxisUsdFormatter(maxStackedValue);

  return (
    <div className="card" style={{ padding: 'var(--loop-space-200)' }}>
      <div style={{ fontSize: '0.875rem', fontWeight: 664, color: 'var(--loop-neutral-900)', marginBottom: 'var(--loop-space-100)' }}>
        Daily spend
      </div>
      <ResponsiveContainer width="100%" height={220}>
        <BarChart data={visible} margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--loop-neutral-200)" />
          <XAxis dataKey="date" tickFormatter={fmtDayTick} tick={{ fontSize: 11, fill: 'var(--loop-neutral-700)' }} />
          <YAxis tickFormatter={fmtAxisUsd} tick={{ fontSize: 11, fill: 'var(--loop-neutral-700)' }} width={56} />
          <Tooltip formatter={(v: number) => fmtUsd(v)} labelStyle={{ color: 'var(--loop-neutral-1100)' }} />
          <Legend wrapperStyle={{ fontSize: '0.75rem' }} />
          <Bar dataKey="auto_approved_usd"  name="Auto-approved"   stackId="a" fill="var(--loop-green-600)" />
          <Bar dataKey="human_required_usd" name="Human-required"  stackId="a" fill="var(--loop-blue-600)" />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
